import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lightbulb } from 'lucide-react';
import { PollarLogoSVG } from '@/constants/logo';
import { trackEvent, EventTypes } from '@/utils/analytics';
import { GoogleLogin } from '@react-oauth/google';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if signup query param is present
  const searchParams = new URLSearchParams(location.search);
  const shouldSignup = searchParams.get('signup') === 'true';
  
  const [isLogin, setIsLogin] = useState(!shouldSignup);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize Facebook SDK
    window.fbAsyncInit = function() {
      window.FB.init({
        appId      : process.env.REACT_APP_FACEBOOK_APP_ID || 'your-facebook-app-id',
        cookie     : true,
        xfbml      : true,
        version    : 'v19.0'
      });
      window.FB.AppEvents.logPageView();
    };

    (function(d, s, id){
       var js, fjs = d.getElementsByTagName(s)[0];
       if (d.getElementById(id)) {return;}
       js = d.createElement(s); js.id = id;
       js.src = "https://connect.facebook.net/en_US/sdk.js";
       fjs.parentNode.insertBefore(js, fjs);
     }(document, 'script', 'facebook-jssdk'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? `${API}/auth/login` : `${API}/auth/register`;
      const response = await axios.post(endpoint, { email, password });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('facilitator', JSON.stringify(response.data.facilitator));

      // Track successful authentication
      if (isLogin) {
        trackEvent(EventTypes.SUCCESSFUL_SIGN_IN, window.location.href);
      } else {
        trackEvent(EventTypes.SUCCESSFUL_SIGN_UP, window.location.href);
        
        // Google Ads conversion tracking for registration
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17773299756/GAxBCLyPsPkbEKyQ_JpC'
          });
        }
      }

      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/social');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google`, {
        credential: credentialResponse.credential
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('facilitator', JSON.stringify(response.data.facilitator));

      // Track event based on whether it's a new user or existing login
      if (response.data.is_new_user) {
        trackEvent(EventTypes.SUCCESSFUL_SIGN_UP, window.location.href);
        
        // Google Ads conversion tracking for registration
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17773299756/GAxBCLyPsPkbEKyQ_JpC'
          });
        }
        
        toast.success('Account created successfully with Google!');
      } else {
        trackEvent(EventTypes.SUCCESSFUL_SIGN_IN, window.location.href);
        toast.success('Successfully signed in with Google!');
      }
      
      navigate('/social');
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(error.response?.data?.detail || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.');
  };

  const handleFacebookLogin = () => {
    if (!window.FB) {
        toast.error("Facebook SDK not loaded.");
        return;
    }
    setLoading(true);
    window.FB.login(function(response) {
        if (response.authResponse) {
            verifyFacebookToken(response.authResponse);
        } else {
            console.log('User cancelled login or did not fully authorize.');
            setLoading(false);
        }
    }, {scope: 'public_profile,email'});
  };

  const verifyFacebookToken = async (authResponse) => {
      try {
          const response = await axios.post(`${API}/auth/facebook`, {
              accessToken: authResponse.accessToken,
              userID: authResponse.userID
          });
          
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('facilitator', JSON.stringify(response.data.facilitator));

          // Track event based on whether it's a new user or existing login
          if (response.data.is_new_user) {
            trackEvent(EventTypes.SUCCESSFUL_SIGN_UP, window.location.href);
            
            // Google Ads conversion tracking for registration
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'conversion', {
                'send_to': 'AW-17773299756/GAxBCLyPsPkbEKyQ_JpC'
              });
            }
            
            toast.success('Account created successfully with Facebook!');
          } else {
            trackEvent(EventTypes.SUCCESSFUL_SIGN_IN, window.location.href);
            toast.success('Successfully signed in with Facebook!');
          }
          
          navigate('/social');
      } catch (error) {
          console.error('Facebook login error:', error);
          toast.error(error.response?.data?.detail || 'Facebook login failed');
      } finally {
          setLoading(false);
      }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex justify-center mb-6">
            <PollarLogoSVG className="w-20 h-20" />
          </div>

          <h1 className="text-3xl font-bold text-center mb-2" style={{ color: '#1a1a1a' }}>
            Pollar
          </h1>
          <p className="text-center text-gray-500 mb-8">
            {isLogin ? 'Welcome back, facilitator' : 'Create your facilitator account'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="facilitator@example.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium py-6 rounded-xl"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="mb-6 flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              type="standard"
              theme="filled_black"
              size="large"
              width="384"
            />
            
            {/* Facebook login temporarily hidden
            <button
              type="button"
              onClick={handleFacebookLogin}
              className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center hover:opacity-90 transition-opacity shadow-sm"
              title="Sign in with Facebook"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="currentColor" strokeWidth="0" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-facebook">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </button>
            */}
          </div>

          <div className="mt-6 text-center space-y-2">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-indigo-600 hover:text-indigo-800 font-medium block w-full"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
            {isLogin && (
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-gray-600 hover:text-gray-800 text-sm block w-full"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
